import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Plus, 
  Search, 
  ArrowUpRight, 
  ArrowDownRight, 
  History, 
  AlertTriangle,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  X,
  Save,
  Loader2,
  FileSpreadsheet,
  Upload,
  Download,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'motion/react';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { 
  collection, 
  query, 
  onSnapshot, 
  orderBy, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  Timestamp, 
  increment,
  limit
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { StockItem, StockTransaction } from '../types';
import { toast } from 'sonner';
import ConfirmationModal from '../components/ConfirmationModal';

interface ParsedStockItem {
  name: string;
  code: string;
  category: string;
  quantity: number;
  unit: string;
  minLevel: number;
  price: number;
  location: string;
  isValid: boolean;
  validationError?: string;
}

const StoreDashboard: React.FC = () => {
  const { profile } = useAuth();
  const [items, setItems] = useState<StockItem[]>([]);
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importedItems, setImportedItems] = useState<ParsedStockItem[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [transactionType, setTransactionType] = useState<'in' | 'out'>('in');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    quantity: 0,
    unit: 'pcs',
    minLevel: 5,
    price: 0,
    location: ''
  });

  const [transactionData, setTransactionData] = useState({
    quantity: 0,
    reason: ''
  });

  const downloadTemplate = () => {
    const templateData = [
      {
        'Item Name': 'Air Conditioner 18000 BTU',
        'Item Code': 'AC-18K',
        'Category': 'HVAC',
        'Quantity': 10,
        'Unit': 'pcs',
        'Minimum Level': 2,
        'Unit Price (TZS)': 850000,
        'Location': 'Shelf A1'
      },
      {
        'Item Name': 'Refrigerant Gas R410A',
        'Item Code': 'GAS-R410',
        'Category': 'Gas & Consumables',
        'Quantity': 25,
        'Unit': 'cylinders',
        'Minimum Level': 5,
        'Unit Price (TZS)': 120000,
        'Location': 'Rack B2'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Template');
    XLSX.writeFile(workbook, 'Stock_Import_Template.xlsx');
    toast.success('Template downloaded successfully');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawRows: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (rawRows.length === 0) {
          toast.error('The uploaded file is empty');
          return;
        }

        const parsed: ParsedStockItem[] = rawRows.map((row) => {
          const name = String(row['Item Name'] || row['Name'] || row['Item'] || row['ITEM NAME'] || row['NAME'] || '').trim();
          const code = String(row['Item Code'] || row['Code'] || row['CODE'] || row['ITEM CODE'] || '').trim();
          const category = String(row['Category'] || row['CATEGORY'] || row['Type'] || 'General').trim() || 'General';
          const rawQty = row['Quantity'] ?? row['QTY'] ?? row['Qty'] ?? row['Quantity In Stock'] ?? 0;
          const quantity = Math.max(0, parseInt(String(rawQty), 10) || 0);
          const unit = String(row['Unit'] || row['UNIT'] || 'pcs').trim() || 'pcs';
          const rawMin = row['Minimum Level'] ?? row['Min Level'] ?? row['MIN LEVEL'] ?? row['Min'] ?? 5;
          const minLevel = Math.max(0, parseInt(String(rawMin), 10) || 5);
          const rawPrice = row['Unit Price (TZS)'] ?? row['Unit Price'] ?? row['Price'] ?? row['PRICE'] ?? 0;
          const price = Math.max(0, parseFloat(String(rawPrice)) || 0);
          const location = String(row['Location'] || row['LOCATION'] || row['Store Location'] || '').trim();

          const isValid = Boolean(name);
          return {
            name,
            code,
            category,
            quantity,
            unit,
            minLevel,
            price,
            location,
            isValid,
            validationError: isValid ? undefined : 'Item Name is missing'
          };
        });

        setImportedItems(parsed);
        toast.success(`Parsed ${parsed.length} items from Excel file`);
      } catch (err) {
        console.error('Error reading Excel file:', err);
        toast.error('Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleBatchImport = async () => {
    const validItems = importedItems.filter(item => item.isValid);
    if (validItems.length === 0) {
      toast.error('No valid items to import');
      return;
    }

    setIsImporting(true);
    let successCount = 0;
    try {
      for (const item of validItems) {
        await addDoc(collection(db, 'stock_items'), {
          name: item.name,
          code: item.code,
          category: item.category,
          quantity: item.quantity,
          unit: item.unit,
          minLevel: item.minLevel,
          price: item.price,
          location: item.location,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        successCount++;
      }
      toast.success(`Successfully imported ${successCount} items into Stock Management!`);
      setIsImportModalOpen(false);
      setImportedItems([]);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import partially failed. Imported ${successCount} items.`);
    } finally {
      setIsImporting(false);
    }
  };

  useEffect(() => {
    const itemsQuery = query(collection(db, 'stock_items'), orderBy('name', 'asc'));
    const itemsUnsubscribe = onSnapshot(itemsQuery, (snapshot) => {
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StockItem)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'stock_items');
    });

    const transactionsQuery = query(collection(db, 'stock_transactions'), orderBy('date', 'desc'), limit(50));
    const transactionsUnsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
      setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StockTransaction)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'stock_transactions');
    });

    return () => {
      itemsUnsubscribe();
      transactionsUnsubscribe();
    };
  }, []);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'stock_items'), {
        ...formData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      toast.success('Item added to stock');
      setIsAddModalOpen(false);
      setFormData({
        name: '',
        code: '',
        category: '',
        quantity: 0,
        unit: 'pcs',
        minLevel: 5,
        price: 0,
        location: ''
      });
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    if (transactionType === 'out' && selectedItem.quantity < transactionData.quantity) {
      toast.error('Insufficient stock');
      return;
    }

    try {
      const itemRef = doc(db, 'stock_items', selectedItem.id);
      const qtyChange = transactionType === 'in' ? transactionData.quantity : -transactionData.quantity;

      await updateDoc(itemRef, {
        quantity: increment(qtyChange),
        updatedAt: Timestamp.now()
      });

      await addDoc(collection(db, 'stock_transactions'), {
        itemId: selectedItem.id,
        itemName: selectedItem.name,
        type: transactionType,
        quantity: transactionData.quantity,
        reason: transactionData.reason,
        performedBy: profile?.uid,
        performedByName: profile?.displayName || profile?.email,
        date: Timestamp.now(),
        createdAt: Timestamp.now()
      });

      toast.success(`Stock ${transactionType === 'in' ? 'restocked' : 'issued'}`);
      setIsTransactionModalOpen(false);
      setTransactionData({ quantity: 0, reason: '' });
      setSelectedItem(null);
    } catch (error) {
      toast.error('Transaction failed');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         item.code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const lowStockItems = items.filter(item => item.quantity <= item.minLevel);
  const categories = Array.from(new Set(items.map(item => item.category)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-blue-900 tracking-tight">Stock Management</h1>
          <p className="text-slate-500 font-medium tracking-tight">Monitor and manage inventory levels</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <button 
            onClick={() => {
              setImportedItems([]);
              setIsImportModalOpen(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-2xl font-bold hover:bg-emerald-800 transition-all shadow-lg shadow-emerald-100 cursor-pointer"
          >
            <FileSpreadsheet className="w-5 h-5" />
            Import Excel
          </button>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-200 cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Add New Item
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
            <Package className="w-16 h-16 text-blue-900" />
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Items</p>
          <h3 className="text-4xl font-black text-blue-900">{items.length}</h3>
          <p className="text-xs text-slate-500 mt-2 font-bold uppercase tracking-widest">In Inventory</p>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
            <AlertTriangle className="w-16 h-16 text-red-600" />
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 text-red-600">Low Stock Alerts</p>
          <h3 className="text-4xl font-black text-red-600">{lowStockItems.length}</h3>
          <p className="text-xs text-red-400 mt-2 font-bold uppercase tracking-widest italic">Requires Attention</p>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
            <History className="w-16 h-16 text-emerald-600" />
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Recent Moves</p>
          <h3 className="text-4xl font-black text-emerald-600">{transactions.length}</h3>
          <p className="text-xs text-emerald-500 mt-2 font-bold uppercase tracking-widest">Past 30 Days</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input 
            type="text"
            placeholder="Search by name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl font-medium focus:outline-none focus:ring-2 focus:ring-blue-900 transition-all shadow-sm"
          />
        </div>
        <div className="relative inline-block min-w-[200px]">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <select 
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full pl-10 pr-10 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-900 appearance-none shadow-sm cursor-pointer"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Stock Items Table */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Item Details</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Category</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Stock Level</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.map(item => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-xl ${item.quantity <= item.minLevel ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                            <Package className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{item.name}</p>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.code || 'NO CODE'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center">
                          <span className={`text-sm font-black ${item.quantity <= item.minLevel ? 'text-red-600' : 'text-slate-900'}`}>
                            {item.quantity} {item.unit}
                          </span>
                          {item.quantity <= item.minLevel && (
                            <span className="text-[8px] font-black text-red-500 uppercase tracking-widest animate-pulse">Low Stock</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => {
                              setSelectedItem(item);
                              setTransactionType('in');
                              setIsTransactionModalOpen(true);
                            }}
                            className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-xl transition-colors"
                            title="Restock"
                          >
                            <ArrowUpRight className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedItem(item);
                              setTransactionType('out');
                              setIsTransactionModalOpen(true);
                            }}
                            className="p-2 hover:bg-orange-50 text-orange-600 rounded-xl transition-colors"
                            title="Issue"
                          >
                            <ArrowDownRight className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedItem(item);
                              setFormData({
                                name: item.name,
                                code: item.code || '',
                                category: item.category,
                                quantity: item.quantity,
                                unit: item.unit,
                                minLevel: item.minLevel,
                                price: item.price,
                                location: item.location || ''
                              });
                              setIsAddModalOpen(true);
                            }}
                            className="p-2 hover:bg-blue-50 text-blue-600 rounded-xl transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm overflow-hidden h-fit">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-blue-900 flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Activity
              </h2>
            </div>
            <div className="space-y-6">
              {transactions.map(transaction => (
                <div key={transaction.id} className="flex gap-4">
                  <div className={`mt-1 p-2 rounded-xl shrink-0 h-fit ${
                    transaction.type === 'in' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'
                  }`}>
                    {transaction.type === 'in' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      {transaction.type === 'in' ? 'Restocked' : 'Issued'} {transaction.quantity} of {(transaction as any).itemName}
                    </p>
                    <p className="text-xs text-slate-500 font-medium">{transaction.reason}</p>
                    <div className="flex items-center gap-2 mt-1">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                         {transaction.performedByName}
                       </p>
                       <span className="w-1 h-1 rounded-full bg-slate-300" />
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                         {transaction.date?.toDate?.()?.toLocaleDateString() || 'N/A'}
                       </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isAddModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsAddModalOpen(false);
                setSelectedItem(null);
              }}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <div className="p-10">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-black text-blue-900 tracking-tight">
                    {selectedItem ? 'Edit Stock Item' : 'Add New Stock Item'}
                  </h3>
                  <button 
                    onClick={() => {
                      setIsAddModalOpen(false);
                      setSelectedItem(null);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>

                <form onSubmit={selectedItem ? async (e) => {
                  e.preventDefault();
                  try {
                    await updateDoc(doc(db, 'stock_items', selectedItem.id), {
                      ...formData,
                      updatedAt: Timestamp.now()
                    });
                    toast.success('Item updated');
                    setIsAddModalOpen(false);
                    setSelectedItem(null);
                  } catch (error) {
                    toast.error('Update failed');
                  }
                } : handleAddItem} className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Item Name</label>
                       <input 
                         type="text" 
                         required
                         value={formData.name}
                         onChange={e => setFormData({...formData, name: e.target.value})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Item Code</label>
                       <input 
                         type="text" 
                         value={formData.code}
                         onChange={e => setFormData({...formData, code: e.target.value})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Category</label>
                       <input 
                         type="text" 
                         required
                         list="categories-list"
                         value={formData.category}
                         onChange={e => setFormData({...formData, category: e.target.value})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                       <datalist id="categories-list">
                         {categories.map(c => <option key={c} value={c} />)}
                       </datalist>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Location in Store</label>
                       <input 
                         type="text" 
                         value={formData.location}
                         onChange={e => setFormData({...formData, location: e.target.value})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                    </div>
                  </div>

                  {!selectedItem && (
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Initial Quantity</label>
                         <input 
                           type="number" 
                           required
                           min="0"
                           value={formData.quantity}
                           onChange={e => setFormData({...formData, quantity: parseInt(e.target.value)})}
                           className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Unit (pcs, kg, etc)</label>
                         <input 
                           type="text" 
                           required
                           value={formData.unit}
                           onChange={e => setFormData({...formData, unit: e.target.value})}
                           className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                         />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Minimum Stock Level</label>
                       <input 
                         type="number" 
                         required
                         min="0"
                         value={formData.minLevel}
                         onChange={e => setFormData({...formData, minLevel: parseInt(e.target.value)})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Unit Price (TZS)</label>
                       <input 
                         type="number" 
                         required
                         min="0"
                         value={formData.price}
                         onChange={e => setFormData({...formData, price: parseInt(e.target.value)})}
                         className="w-full px-4 py-3 bg-slate-50 border-none rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all"
                       />
                    </div>
                  </div>

                  <div className="pt-6">
                    <button 
                      type="submit"
                      className="w-full bg-blue-900 text-white py-4 rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-200"
                    >
                      {selectedItem ? 'Save Changes' : 'Add Item'}
                    </button>
                    {selectedItem && (
                      <button 
                        type="button"
                        onClick={() => {
                          setConfirmModal({
                            isOpen: true,
                            title: 'Delete Stock Item',
                            message: 'Are you sure you want to delete this item? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                await deleteDoc(doc(db, 'stock_items', selectedItem.id));
                                toast.success('Item deleted');
                                setIsAddModalOpen(false);
                                setSelectedItem(null);
                              } catch (error) {
                                toast.error('Failed to delete item');
                              }
                            }
                          });
                        }}
                        className="w-full mt-4 bg-red-50 text-red-600 py-4 rounded-2xl font-bold hover:bg-red-100 transition-all cursor-pointer"
                      >
                        Delete Item
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Transaction Modal (Restock/Issue) */}
      <AnimatePresence>
        {isTransactionModalOpen && selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTransactionModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <div className="p-10 text-center">
                <div className={`w-20 h-20 mx-auto rounded-[28px] flex items-center justify-center mb-6 ${
                  transactionType === 'in' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'
                }`}>
                  {transactionType === 'in' ? <ArrowUpRight className="w-10 h-10" /> : <ArrowDownRight className="w-10 h-10" />}
                </div>
                
                <h3 className="text-2xl font-black text-blue-900 tracking-tight mb-2">
                  {transactionType === 'in' ? 'Restock Item' : 'Issue Item'}
                </h3>
                <p className="text-slate-500 font-medium mb-8">
                  {selectedItem.name} ({selectedItem.quantity} {selectedItem.unit} currently)
                </p>

                <form onSubmit={handleTransaction} className="space-y-6 text-left">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Quantity</label>
                    <input 
                      type="number" 
                      required
                      min="1"
                      value={transactionData.quantity || ''}
                      onChange={e => setTransactionData({...transactionData, quantity: parseInt(e.target.value)})}
                      className="w-full px-4 py-4 bg-slate-50 border-none rounded-2xl font-bold text-slate-900 text-center text-2xl focus:ring-2 focus:ring-blue-900 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Reason / Reference</label>
                    <textarea 
                      required
                      placeholder={transactionType === 'in' ? 'e.g., Purchase from Vendor' : 'e.g., Issued for Client XYZ'}
                      value={transactionData.reason}
                      onChange={e => setTransactionData({...transactionData, reason: e.target.value})}
                      className="w-full px-4 py-4 bg-slate-50 border-none rounded-2xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-900 transition-all h-24"
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button 
                      type="button"
                      onClick={() => setIsTransactionModalOpen(false)}
                      className="flex-1 bg-slate-100 text-slate-600 py-4 rounded-2xl font-bold hover:bg-slate-200 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className={`flex-1 text-white py-4 rounded-2xl font-bold transition-all shadow-lg ${
                        transactionType === 'in' ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-200' : 'bg-orange-500 hover:bg-orange-600 shadow-orange-200'
                      }`}
                    >
                      Confirm
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Import Excel Modal */}
      <AnimatePresence>
        {isImportModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                if (!isImporting) {
                  setIsImportModalOpen(false);
                  setImportedItems([]);
                }
              }}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[40px] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-50 text-emerald-700 rounded-2xl">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-blue-900 tracking-tight">Import Stock Data from Excel</h3>
                    <p className="text-slate-500 text-sm font-medium">Upload an Excel (.xlsx, .xls) or CSV file to batch add items</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setIsImportModalOpen(false);
                    setImportedItems([]);
                  }}
                  disabled={isImporting}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="p-8 overflow-y-auto space-y-6 flex-1">
                {/* Actions & Dropzone */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                  <div className="space-y-1 text-center sm:text-left">
                    <p className="font-bold text-slate-800 text-sm">Need a template?</p>
                    <p className="text-xs text-slate-500">Download our sample spreadsheet formatted with the correct columns.</p>
                  </div>
                  <button
                    onClick={downloadTemplate}
                    type="button"
                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold text-xs hover:bg-slate-100 transition-all cursor-pointer shadow-sm shrink-0"
                  >
                    <Download className="w-4 h-4 text-emerald-600" />
                    Download Sample Template
                  </button>
                </div>

                <div className="relative border-2 border-dashed border-slate-200 hover:border-emerald-500 bg-slate-50/50 rounded-3xl p-8 text-center transition-all group">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    disabled={isImporting}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Upload className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">Click or Drag & Drop Excel File</p>
                      <p className="text-xs text-slate-400 mt-1">Supports .xlsx, .xls, and .csv files</p>
                    </div>
                  </div>
                </div>

                {/* Preview Table */}
                {importedItems.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-slate-900 text-base">Preview ({importedItems.length} items found)</span>
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                          {importedItems.filter(i => i.isValid).length} Valid
                        </span>
                        {importedItems.filter(i => !i.isValid).length > 0 && (
                          <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-bold">
                            {importedItems.filter(i => !i.isValid).length} Invalid
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setImportedItems([])}
                        className="text-xs font-bold text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="border border-slate-200 rounded-2xl overflow-hidden max-h-64 overflow-y-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-100 sticky top-0 font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                          <tr>
                            <th className="p-3">Status</th>
                            <th className="p-3">Item Name</th>
                            <th className="p-3">Code</th>
                            <th className="p-3">Category</th>
                            <th className="p-3">Qty</th>
                            <th className="p-3">Unit</th>
                            <th className="p-3">Min Level</th>
                            <th className="p-3">Price (TZS)</th>
                            <th className="p-3">Location</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white font-medium">
                          {importedItems.map((item, idx) => (
                            <tr key={idx} className={item.isValid ? 'hover:bg-slate-50' : 'bg-red-50/50'}>
                              <td className="p-3">
                                {item.isValid ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <span className="flex items-center gap-1 text-red-600 font-bold" title={item.validationError}>
                                    <AlertCircle className="w-4 h-4" />
                                  </span>
                                )}
                              </td>
                              <td className="p-3 font-bold text-slate-900">{item.name || <span className="text-red-400 italic">Missing</span>}</td>
                              <td className="p-3 text-slate-600">{item.code || '-'}</td>
                              <td className="p-3 text-slate-600">{item.category}</td>
                              <td className="p-3 font-bold text-slate-900">{item.quantity}</td>
                              <td className="p-3 text-slate-600">{item.unit}</td>
                              <td className="p-3 text-slate-600">{item.minLevel}</td>
                              <td className="p-3 text-slate-900 font-bold">{item.price.toLocaleString()}</td>
                              <td className="p-3 text-slate-600">{item.location || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsImportModalOpen(false);
                    setImportedItems([]);
                  }}
                  disabled={isImporting}
                  className="px-6 py-3 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-all cursor-pointer text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBatchImport}
                  disabled={isImporting || importedItems.filter(i => i.isValid).length === 0}
                  className="px-8 py-3 rounded-xl bg-emerald-700 text-white font-bold hover:bg-emerald-800 transition-all shadow-lg shadow-emerald-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4" />
                      Import {importedItems.filter(i => i.isValid).length} Valid Items
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
    </div>
  );
};

export default StoreDashboard;
